import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { RouteIntervalProgress } from "../../app/models/responseModels/DdRouteProgressResponse";

interface RouteProgressChartProps {
  data: RouteIntervalProgress[];
}
const RouteProgressChart: React.FC<RouteProgressChartProps> = ({ data }) => {
  // Default data in case the input data is empty
  const defaultData = [
    {
      scheduleTime: new Date().toISOString(),
      packagesDeliveredInLast30Min: 0,
      cumulativeDelivered: 0,
    },
    {
      scheduleTime: new Date(Date.now() + 30 * 60 * 1000).toISOString(),
      packagesDeliveredInLast30Min: 0,
      cumulativeDelivered: 0,
    },
  ];

  // If data is empty, fallback to defaultData
  const chartData = data.length > 0 ? data : defaultData;
  const cumulativeData = useMemo(() => {
    let totalDelivered = 0;
    return chartData.map((point) => {
      totalDelivered += point.packagesDeliveredInLast30Min;
      return { ...point, cumulativeDelivered: totalDelivered };
    });
  }, [chartData]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const formatDateWithYear = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const {
        scheduleTime,
        packagesDeliveredInLast30Min,
        cumulativeDelivered,
      } = payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "#fff",
            padding: "10px",
            border: "1px solid #ccc",
          }}
        >
          <p
            style={{ color: "blue", fontWeight: "bold" }}
          >{`Time: ${formatDateWithYear(scheduleTime)}`}</p>
          <p
            style={{ color: "green", fontWeight: "bold" }}
          >{`Packages Delivered in Last 30 Minutes: ${packagesDeliveredInLast30Min}`}</p>
          <p
            style={{ color: "#FFD700", fontWeight: "bold" }}
          >{`Total Packages Delivered: ${cumulativeDelivered}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart
        data={cumulativeData}
        margin={{
          top: 5,
          right: 30,
          left: 20,
          bottom: 40,
        }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="scheduleTime"
          tickFormatter={formatDate}
          interval={1}
          angle={-30}
          textAnchor="end"
          label={{
            value: "Date & Time",
            position: "insideBottomRight",
            offset: -30,
          }}
        />
        <YAxis
          label={{
            value: "Packages Delivered",
            angle: -90,
            position: "insideLeft",
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend verticalAlign="top" />
        <Line
          type="monotone"
          dataKey="packagesDeliveredInLast30Min"
          name="Packages delivered in last 30 mins"
          stroke="#8884d8"
          activeDot={{ r: 8 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default RouteProgressChart;
